import React, { useEffect, useState } from 'react';
import { func, bool, string, shape } from 'prop-types';
import { validate as validator } from 'ui-validator';
import { heapTrackErrors } from '@shared_modules/baui-heap-tracking';
import { trackHeapEvent } from '@shared_modules/custom-heap-tracking-attributes';
import LoanTypeSelectionCards from '@shared_modules/pi1/components/loan-type-selection-cards/LoanTypeSelectionCards';
import { Page } from '../../components';
import validations from './loan-type-validations';
import { submitHeapTracking, truncateLongName, trackAppStart } from '../../utils/util';
import useTimeOnPage from '../../hooks/useTimeOnPage.js';
import TealiumTrack from '@shared_modules/baui-utils/client/utils/tealium-track';
import { DM_CODE_LEADS_REDIRECT_URL } from '../../../direct-mail/client/constants/dmCode';
import { dmType } from '../../../../shared_modules/direct-mail/constants';

const LoanType = ({
  loanType: initialLoanType,
  onIncomeAdditionalClick,
  nextLocation,
  previousLocation,
  saveValues,
  primaryName,
  isFromSecondLoanFlow,
  dm,
}) => {
  // also renders twice on back and in the tests it logs 3x on load
  // in the tests, the submit is not disabled if there is a default
  // loantype. how does it get disabled in the actual browser? strange.
  // loanType from routing.jsx is undefined too, correctly
  const [loanType, setLoanType] = useState(!initialLoanType ? 'individual' : initialLoanType);
  const [validationErrors, setValidationErrors] = useState({});
  const [disableSubmit, setDisableSubmit] = useState(!loanType);
  const isFirstPage = isFromSecondLoanFlow || dm.isDm;
  const goBack = (dm.isDm || !isFromSecondLoanFlow) && previousLocation;

  useEffect(() => {
    if (isFromSecondLoanFlow) trackAppStart({ isDm: dm.isDm });
  }, []);

  useTimeOnPage({ eventName: 'loanType cta', payload: { loanType } });

  const getPageTitle = () => {
    let pageProps = {
      header: '',
      showDisclaimerContent: true,
      showSingleDisclaimer: true,
    };

    let primaryFirstName = '';
    let welcomeBackText = '';
    if (primaryName.firstName) {
      primaryFirstName = truncateLongName(primaryName.firstName);
      welcomeBackText = `Welcome, ${primaryFirstName}! `;
    }

    pageProps.header = welcomeBackText + 'How would you like to apply?';
    if (primaryName.lastName) {
      const primaryLastName = truncateLongName(primaryName.lastName);
      pageProps.title = `Welcome, ${primaryFirstName} ${primaryLastName}!`;
    }

    if (!isFromSecondLoanFlow && !dm.isDm) {
      pageProps.title = `${primaryFirstName}, are you applying with someone else?`;
      pageProps.header = '';
      if (loanType === 'joint') {
        pageProps.showDisclaimerContent = false;
        pageProps.showSingleDisclaimer = false;
      } else {
        pageProps.submitLabel = 'Get Your Rate';
      }
    }

    if (dm.isDm && !dm.hasPartialDMPII) {
      pageProps.header = (
        <div>
          {pageProps.header}
          <a
            href={
              dm.leadType === dmType.PROSPECT
                ? `${DM_CODE_LEADS_REDIRECT_URL}${window.location.search}`
                : '/site/borrow/personal-loans?lc_referrer=DM_SPECIAL'
            }
          >
            <strong> Not you? Let’s sort that out.</strong>
          </a>
        </div>
      );
    }

    return pageProps;
  };

  const handleClick = event => {
    const value = event.target.value;
    setLoanType(value);
    setDisableSubmit(false);
    saveValues({ loan: { loanType: value } }); // was logging the default value rather than updated value, since not waiting for setLoanType result
    trackHeapEvent('P1-1QAAT click loanType', value);
  };

  const submit = e => {
    if (e) e.preventDefault();
    const errors = validator({ loanType }, validations);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      heapTrackErrors(errors);
    } else {
      const isJoint = loanType === 'joint';
      // remove the Additional Income step if user is converting to Joint
      if (isJoint) {
        onIncomeAdditionalClick({ showIncomeAdditional: false });
      }
      saveValues({ loan: { loanType } });
      if (isFirstPage) {
        TealiumTrack.link({ PI1_Start: 'true' });
      } else if (!isJoint) submitHeapTracking(isJoint);
      nextLocation();
    }
  };

  return (
    <Page
      name="loan-type"
      goBack={goBack}
      submitHandler={submit}
      disableSubmit={disableSubmit}
      loanType={loanType}
      {...getPageTitle()}
      submitBtnClassName={goBack ? undefined : 'submit-btn--noMarginLeft'}
      isLoanTypePage
      showNotificationBanner={isFirstPage}
    >
      <LoanTypeSelectionCards loanType={loanType === '' ? 'individual' : loanType} onClick={handleClick} />
    </Page>
  );
};

LoanType.propTypes = {
  saveValues: func.isRequired,
  previousLocation: func.isRequired,
  nextLocation: func.isRequired,
  onIncomeAdditionalClick: func.isRequired,
  loanType: string,
  dm: shape({
    isDm: bool,
    isExperianDm: bool,
  }),
  primaryName: shape({
    firstName: string,
    lastName: string,
  }),
};

LoanType.defaultProps = {
  loanType: '',
  dm: {
    isDm: false,
    isExperianDm: false,
  },
  primaryName: {
    firstName: '',
    lastName: '',
  },
};

export default LoanType;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pages/LoanType/LoanType.scss');